import { useMutation, useQuery } from '@tanstack/react-query';

import { useQueryInvalidation } from '@pkg/hooks';

import { type EidCategory, type EmojiId } from '@app/client';
import {
    callGetExtendedData,
    callUpdateEmojiFeatures,
    type UpdateFeaturesParams,
} from '@app/client/resources/admin/emojis';
import {
    callDeleteEmojiTag,
    callGetEmojiTags,
    callUpdateEmojiTags,
    type UpdateEmojiTagsParams,
} from '@app/client/resources/emojis/emoji-tags';

const EMOJI_TAG_CACHE = 'emojiTags';
const EMOJI_CACHE = 'emoji';
export function useUpdateEmoji() {
    const invalidateEmoji = useQueryInvalidation([EMOJI_TAG_CACHE, EMOJI_CACHE]);
    const { mutateAsync: updateFeatures } = useMutation({
        mutationFn: ({ value, id }: UpdateFeaturesParams) => callUpdateEmojiFeatures({ value, id }),
        onSuccess: invalidateEmoji,
    });

    const { mutateAsync: updateTags } = useMutation({
        mutationFn: ({ value, eid }: UpdateEmojiTagsParams) => callUpdateEmojiTags({ value, eid }),
        onSuccess: invalidateEmoji,
    });
    const { mutateAsync: deleteTag } = useMutation({
        mutationFn: ({ value, eid }: UpdateEmojiTagsParams) => callDeleteEmojiTag({ value, eid }),
        onSuccess: invalidateEmoji,
    });

    return { updateFeatures, updateTags, deleteTag };
}

export function useGetEmojiTags({ eid = '', tagsArray = [] }: { eid?: EmojiId; tagsArray?: EidCategory[] }) {
    const tags = tagsArray ? tagsArray.join(',') : undefined;
    const emojiTagCache = [EMOJI_TAG_CACHE, eid, tags];
    return useQuery({
        queryKey: emojiTagCache,
        queryFn: ({ signal }) => callGetEmojiTags({ eid, query: { tags }, settings: { signal } }),
        enabled: !!eid?.length,
        retry: false,
        select: (r) => {
            if (r?.ok && r.value?.result) {
                return r.value.result;
            }
        },
    });
}

export function useGetExtendedEmoji({ yat = '' }: { yat?: EmojiId }) {
    const emojiCache = [EMOJI_CACHE, yat];
    return useQuery({
        queryKey: emojiCache,
        queryFn: ({ signal }) => callGetExtendedData({ yat, settings: { signal } }),
        enabled: !!yat?.length,
        retry: false,
        select: (r) => {
            if (r?.ok && r.value) {
                return r.value;
            }
        },
    });
}
